/**
 * WelcomeStep.jsx
 * ===============
 * Welcome screen explaining what's next in the setup wizard
 */

import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import SetupWizard from './SetupWizard';

const WelcomeStep = () => {
  const { appName } = useParams();
  const [searchParams] = useSearchParams();
  const appType = searchParams.get('appType') || 'webapp';

  const getFeatures = () => {
    if (appType === 'static') {
      return [
        { icon: '🖼️', label: 'Upload Assets', description: 'Add images and files' },
        { icon: '✨', label: 'AI Generation', description: 'AI creates your pages' },
        { icon: '📄', label: 'Design Pages', description: 'Customize your content' },
        { icon: '🔗', label: 'Integrations', description: 'Payment, SEO, WhatsApp, Social' }
      ];
    } else if (appType === 'webapp') {
      return [
        { icon: '🖼️', label: 'Upload Assets', description: 'Add images and files' },
        { icon: '✨', label: 'AI Generation', description: 'AI creates your pages' },
        { icon: '💾', label: 'Setup Data', description: 'Create data tables' },
        { icon: '📄', label: 'Design Pages', description: 'Customize pages and forms' },
        { icon: '✅', label: 'Add Validations', description: 'Smart form rules' },
        { icon: '⚡', label: 'Configure Actions', description: 'Interactive buttons' }
      ];
    } else {
      return [
        { icon: '🖼️', label: 'Upload Assets', description: 'Add images and files' },
        { icon: '✨', label: 'AI Generation', description: 'AI creates your pages' },
        { icon: '💾', label: 'Setup Data', description: 'Create data tables' },
        { icon: '📄', label: 'Design Pages', description: 'Customize pages and forms' },
        { icon: '✅', label: 'Add Validations', description: 'Smart form rules' },
        { icon: '⚡', label: 'Configure Actions', description: 'Interactive buttons' },
        { icon: '🔄', label: 'Build Processes', description: 'Multi-step workflows' }
      ];
    }
  };

  const features = getFeatures();
  const estimatedTime = appType === 'static' ? '15' : appType === 'webapp' ? '20' : '30';

  return (
    <SetupWizard currentStepKey="welcome">
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '40px 20px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '64px', marginBottom: '24px' }}>
          {appType === 'static' ? '🌐' : appType === 'webapp' ? '📱' : '🏢'}
        </div>
        
        <h2 style={{
          fontSize: '32px',
          fontWeight: '700',
          color: '#111827',
          marginBottom: '16px'
        }}>
          Welcome to {appName}!
        </h2>

        <p style={{
          fontSize: '18px',
          color: '#6B7280',
          marginBottom: '40px',
          lineHeight: '1.6'
        }}>
          Your {appType === 'static' ? 'static website' : 
                appType === 'webapp' ? 'web application' : 
                'enterprise application'} has been created. Let's set it up together.
        </p>

        <div style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #E5E7EB',
          padding: '32px',
          marginBottom: '40px'
        }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#111827',
            marginBottom: '24px'
          }}>
            What's Next?
          </h3>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px'
          }}>
            {features.map((feature, index) => (
              <div
                key={index}
                style={{
                  padding: '20px',
                  backgroundColor: '#F9FAFB',
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB'
                }}
              >
                <div style={{ fontSize: '32px', marginBottom: '12px' }}>
                  {feature.icon}
                </div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#111827',
                  marginBottom: '4px'
                }}>
                  {feature.label}
                </div>
                <div style={{ fontSize: '12px', color: '#6B7280' }}>
                  {feature.description}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div style={{
          padding: '20px',
          backgroundColor: '#EFF6FF',
          borderRadius: '8px',
          border: '1px solid #DBEAFE',
          marginBottom: '24px'
        }}>
          <div style={{ fontSize: '14px', color: '#1E40AF', fontWeight: '600' }}>
            ⏱️ Estimated time: {estimatedTime} minutes
          </div>
          <div style={{ fontSize: '12px', color: '#3B82F6', marginTop: '4px' }}>
            Don't worry - you can skip any step and come back later
          </div>
        </div>

        <div style={{
          fontSize: '14px',
          color: '#6B7280',
          fontStyle: 'italic'
        }}>
          💡 Tip: After you upload assets, our AI will analyze them and generate 
          custom pages tailored to your content. Then you can refine as needed!
        </div>
      </div>
    </SetupWizard>
  );
};

export default WelcomeStep;

